import type {
  Currency,
  ImportedPosition,
  NativeFlowBreakdown,
  NativeProfitAttributionBucket,
  NativeReturnEvidence,
  ProfitAttributionBreakdown,
  ProfitAttributionGapReason,
  PortfolioAnalytics,
  PortfolioAnalyticsDimension,
  PortfolioAnalyticsPoint,
  PortfolioAnalyticsRange,
  PortfolioAnalyticsRangeDimension,
  PortfolioAnalyticsRangeKey,
  PortfolioAnalyticsFilters,
  PositionGroup,
  PositionPerformancePoint,
} from "./portfolio-types";
import { dateLabelBounds, resolveTimeRange, shiftCalendarMonths, type TimeRangeValue } from "./time-range.ts";
import {
  emptyNativeFlowBreakdown,
  mergeNativeFlowBreakdowns,
  nativeFlowBreakdownFromAmounts,
  scaleNativeFlowBreakdown,
} from "./native-investment-flows.ts";
import { underlyingExposureKey, unpenetratedExposureLabel } from "./position-classification.ts";
import {
  resolvePositionUnderlyingExposures,
  type AssetExposure,
  type ResolvedPositionExposureSet,
} from "./strategy-sleeves.ts";
import { isTigerCashPosition } from "./position-performance.ts";
import {
  buildNativeValuationBreakdown,
  emptyNativeValuationBreakdown,
  type NativeValuationInput,
} from "./native-valuations.ts";

type DimensionName = keyof PortfolioAnalytics["dimensions"];
type PeriodName = keyof PortfolioAnalytics["series"];
type NormalizedPositionPoint = {
  date: string;
  profitCny: number;
  addedAmountCny: number;
  reducedAmountCny: number;
  incomeCny: number;
  capitalBaseCny: number;
  capitalBaseMeasured: boolean;
  marketValue: number | null;
  marketValueCny: number | null;
  valuationRateCny: number | null;
  valuationRateAt: string | null;
  valuationRateSource: string | null;
  valuationRateMethod: PositionPerformancePoint["valuationRateMethod"];
  quantity: number | null;
  measured: boolean;
  flowMeasured: boolean;
  flowHasActivity: boolean;
  historicalEvidence: boolean;
  nativeFlows: NativeFlowBreakdown;
  nativeReturn: NativeReturnEvidence;
  profitAttribution: ProfitAttributionBreakdown;
  valuationEvidence: boolean;
  groupId: string | null;
  groupName: string | null;
  groupColor: string | null;
};

type ExposureStatus = ResolvedPositionExposureSet["status"];

function scaleNativeReturnEvidence(input: NativeReturnEvidence, weight: number): NativeReturnEvidence {
  return {
    ...input,
    profitAmount: input.profitAmount == null ? null : input.profitAmount * weight,
    capitalBaseAmount: input.capitalBaseAmount == null ? null : input.capitalBaseAmount * weight,
  };
}

function scaleProfitAttributionBreakdown(
  input: ProfitAttributionBreakdown,
  weight: number,
): ProfitAttributionBreakdown {
  const scaled = (value: number | null) => value == null ? null : value * weight;
  return {
    ...input,
    buckets: input.buckets.map((bucket) => ({
      ...bucket,
      nativeProfitAmount: scaled(bucket.nativeProfitAmount),
      investmentProfitCny: scaled(bucket.investmentProfitCny),
      fxEffectCny: scaled(bucket.fxEffectCny),
      totalProfitCny: scaled(bucket.totalProfitCny),
      knownNativeProfitAmount: scaled(bucket.knownNativeProfitAmount ?? null),
      knownInvestmentProfitCny: scaled(bucket.knownInvestmentProfitCny ?? null),
      knownFxEffectCny: scaled(bucket.knownFxEffectCny ?? null),
      knownTotalProfitCny: scaled(bucket.knownTotalProfitCny ?? null),
    })),
    investmentProfitCny: scaled(input.investmentProfitCny),
    fxEffectCny: scaled(input.fxEffectCny),
    totalProfitCny: scaled(input.totalProfitCny),
    knownInvestmentProfitCny: scaled(input.knownInvestmentProfitCny ?? null),
    knownFxEffectCny: scaled(input.knownFxEffectCny ?? null),
    knownTotalProfitCny: scaled(input.knownTotalProfitCny ?? null),
  };
}

function scaleNormalizedPositionPoint(point: NormalizedPositionPoint, weight: number): NormalizedPositionPoint {
  return {
    ...point,
    profitCny: point.profitCny * weight,
    addedAmountCny: point.addedAmountCny * weight,
    reducedAmountCny: point.reducedAmountCny * weight,
    incomeCny: point.incomeCny * weight,
    capitalBaseCny: point.capitalBaseCny * weight,
    marketValue: point.marketValue == null ? null : point.marketValue * weight,
    marketValueCny: point.marketValueCny == null ? null : point.marketValueCny * weight,
    quantity: point.quantity == null ? null : point.quantity * weight,
    nativeFlows: scaleNativeFlowBreakdown(point.nativeFlows, weight),
    nativeReturn: scaleNativeReturnEvidence(point.nativeReturn, weight),
    profitAttribution: scaleProfitAttributionBreakdown(point.profitAttribution, weight),
  };
}

const exposureStatusRank: Record<ExposureStatus, number> = {
  verified: 0,
  product_scope: 1,
  unpenetrated: 2,
  conflict: 3,
};

function lessCertainExposureStatus(left: ExposureStatus, right: ExposureStatus) {
  return exposureStatusRank[left] >= exposureStatusRank[right] ? left : right;
}

const profitAttributionTolerance = 0.000001;

function normalizedAttributionCount(value: number) {
  return Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
}

function profitAttributionCurrencyRank(currency: Currency) {
  if (currency === "CNY") return 0;
  if (currency === "USD") return 1;
  if (currency === "HKD") return 2;
  return 3;
}

function sortProfitAttributionBuckets(buckets: NativeProfitAttributionBucket[]) {
  return buckets.sort((left, right) =>
    profitAttributionCurrencyRank(left.currency) - profitAttributionCurrencyRank(right.currency)
    || left.currency.localeCompare(right.currency));
}

function mergeProfitAttributionGapReasons(
  reasons: ReadonlyArray<readonly ProfitAttributionGapReason[] | null | undefined>,
) {
  return [...new Set(reasons.flatMap((value) => value ?? []))];
}

export function unavailableProfitAttribution(
  totalEpisodeCount = 0,
  currency: Currency | null = null,
): ProfitAttributionBreakdown {
  const total = normalizedAttributionCount(totalEpisodeCount);
  return {
    buckets: currency && total > 0 ? [{
      currency,
      nativeProfitAmount: null,
      investmentProfitCny: null,
      fxEffectCny: null,
      totalProfitCny: null,
      knownNativeProfitAmount: null,
      knownInvestmentProfitCny: null,
      knownFxEffectCny: null,
      knownTotalProfitCny: null,
      gapReasons: ["evidence-missing"],
      knownEpisodeCount: 0,
      totalEpisodeCount: total,
      status: "unavailable",
    }] : [],
    investmentProfitCny: null,
    fxEffectCny: null,
    totalProfitCny: null,
    knownInvestmentProfitCny: null,
    knownFxEffectCny: null,
    knownTotalProfitCny: null,
    gapReasons: total > 0 ? ["evidence-missing"] : [],
    knownEpisodeCount: 0,
    totalEpisodeCount: total,
    status: "unavailable",
  };
}

function nonYieldCashProfitAttribution(): ProfitAttributionBreakdown {
  return {
    ...unavailableProfitAttribution(),
    notApplicableEpisodeCount: 1,
    notApplicableReasons: ["non-yield-cash"],
  };
}

export function profitAttributionForPositionPoint(input: {
  currency: Currency;
  nativeProfitAmount: number | null | undefined;
  totalProfitCny: number | null | undefined;
  valuationRateCny: number | null | undefined;
  weight?: number;
  attributable?: boolean;
}): ProfitAttributionBreakdown {
  const weight = Number.isFinite(input.weight) ? Math.max(0, Number(input.weight)) : 1;
  const nativeKnown = input.nativeProfitAmount != null && Number.isFinite(input.nativeProfitAmount);
  const totalKnown = input.totalProfitCny != null && Number.isFinite(input.totalProfitCny);
  const rate = input.currency === "CNY" ? 1 : input.valuationRateCny;
  const rateKnown = rate != null && Number.isFinite(rate) && rate > 0;
  const attributable = input.attributable !== false;
  const nativeProfitAmount = nativeKnown ? Number(input.nativeProfitAmount) * weight : null;
  const investmentProfitCny = nativeKnown && rateKnown
    ? Number(input.nativeProfitAmount) * Number(rate) * weight : null;
  const totalProfitCny = totalKnown ? Number(input.totalProfitCny) * weight : null;
  const complete = attributable && nativeProfitAmount != null && investmentProfitCny != null && totalProfitCny != null;
  const fxEffectCny = complete ? totalProfitCny - investmentProfitCny : null;
  const gapReasons: ProfitAttributionGapReason[] = complete ? [] : [
    ...(!attributable ? ["scope-attribution-mismatch" as const] : []),
    ...(!nativeKnown ? ["native-profit-missing" as const] : []),
    ...(!rateKnown ? ["valuation-rate-missing" as const] : []),
    ...(!totalKnown ? ["total-profit-missing" as const] : []),
  ];
  if (!complete && !gapReasons.length) gapReasons.push("evidence-missing");
  const status = complete ? "complete" as const
    : nativeProfitAmount != null || investmentProfitCny != null || totalProfitCny != null ? "partial" as const
      : "unavailable" as const;
  return {
    buckets: [{
      currency: input.currency,
      nativeProfitAmount,
      investmentProfitCny: attributable ? investmentProfitCny : null,
      fxEffectCny,
      totalProfitCny: attributable ? totalProfitCny : null,
      knownNativeProfitAmount: complete ? nativeProfitAmount : null,
      knownInvestmentProfitCny: complete ? investmentProfitCny : null,
      knownFxEffectCny: complete ? fxEffectCny : null,
      knownTotalProfitCny: complete ? totalProfitCny : null,
      gapReasons,
      knownEpisodeCount: complete ? 1 : 0,
      totalEpisodeCount: 1,
      status,
    }],
    investmentProfitCny: complete ? investmentProfitCny : null,
    fxEffectCny,
    totalProfitCny: complete ? totalProfitCny : null,
    knownInvestmentProfitCny: complete ? investmentProfitCny : null,
    knownFxEffectCny: complete ? fxEffectCny : null,
    knownTotalProfitCny: complete ? totalProfitCny : null,
    gapReasons,
    knownEpisodeCount: complete ? 1 : 0,
    totalEpisodeCount: 1,
    status,
  };
}

export function mergeProfitAttributionBreakdowns(
  inputs: readonly ProfitAttributionBreakdown[],
  options: {
    mode?: "parallel" | "period";
    expectedTotalProfitCny?: number | null;
    totalEpisodeCount?: number;
  } = {},
): ProfitAttributionBreakdown {
  if (!inputs.length) return unavailableProfitAttribution(options.totalEpisodeCount ?? 0);
  const mode = options.mode ?? "parallel";
  const totalEpisodeCount = normalizedAttributionCount(options.totalEpisodeCount ?? (mode === "parallel"
    ? inputs.reduce((sum, input) => sum + input.totalEpisodeCount, 0)
    : Math.max(0, ...inputs.map((input) => input.totalEpisodeCount))));
  const knownEpisodeCount = Math.min(totalEpisodeCount, normalizedAttributionCount(mode === "parallel"
    ? inputs.reduce((sum, input) => sum + input.knownEpisodeCount, 0)
    : Math.min(...inputs.map((input) => input.knownEpisodeCount))));
  const notApplicableEpisodeCount = normalizedAttributionCount(mode === "parallel"
    ? inputs.reduce((sum, input) => sum + (input.notApplicableEpisodeCount ?? 0), 0)
    : Math.max(0, ...inputs.map((input) => input.notApplicableEpisodeCount ?? 0)));
  const notApplicableReasons = [...new Set(inputs.flatMap((input) => input.notApplicableReasons ?? []))];
  const currencies = new Set(inputs.flatMap((input) => input.buckets.map((bucket) => bucket.currency)));
  const buckets = sortProfitAttributionBuckets([...currencies].map((currency) => {
    const values = inputs.flatMap((input) => input.buckets.filter((bucket) => bucket.currency === currency));
    const bucketTotal = normalizedAttributionCount(mode === "parallel"
      ? values.reduce((sum, value) => sum + value.totalEpisodeCount, 0)
      : Math.max(0, ...values.map((value) => value.totalEpisodeCount)));
    const bucketKnown = Math.min(bucketTotal, normalizedAttributionCount(mode === "parallel"
      ? values.reduce((sum, value) => sum + value.knownEpisodeCount, 0)
      : Math.min(...values.map((value) => value.knownEpisodeCount))));
    const sumIfKnown = (select: (value: NativeProfitAttributionBucket) => number | null) =>
      values.length && values.every((value) => select(value) != null)
        ? values.reduce((sum, value) => sum + Number(select(value)), 0)
        : null;
    const nativeProfitAmount = sumIfKnown((value) => value.nativeProfitAmount);
    const investmentProfitCny = sumIfKnown((value) => value.investmentProfitCny);
    const fxEffectCny = sumIfKnown((value) => value.fxEffectCny);
    const totalProfitCny = sumIfKnown((value) => value.totalProfitCny);
    const sumKnown = (
      selectKnown: (value: NativeProfitAttributionBucket) => number | null | undefined,
      selectComplete: (value: NativeProfitAttributionBucket) => number | null,
    ) => {
      const known = values.flatMap((value) => {
        const selected = selectKnown(value);
        const amount = selected === undefined && value.status === "complete" ? selectComplete(value) : selected;
        return amount != null && Number.isFinite(amount) ? [Number(amount)] : [];
      });
      return known.length ? known.reduce((sum, value) => sum + value, 0) : null;
    };
    const knownNativeProfitAmount = sumKnown(
      (value) => value.knownNativeProfitAmount,
      (value) => value.nativeProfitAmount,
    );
    const knownInvestmentProfitCny = sumKnown(
      (value) => value.knownInvestmentProfitCny,
      (value) => value.investmentProfitCny,
    );
    const knownFxEffectCny = sumKnown(
      (value) => value.knownFxEffectCny,
      (value) => value.fxEffectCny,
    );
    const knownTotalProfitCny = sumKnown(
      (value) => value.knownTotalProfitCny,
      (value) => value.totalProfitCny,
    );
    const complete = bucketTotal > 0 && bucketKnown >= bucketTotal
      && values.every((value) => value.status === "complete")
      && nativeProfitAmount != null && investmentProfitCny != null
      && fxEffectCny != null && totalProfitCny != null;
    const partial = values.some((value) => value.status === "partial")
      || nativeProfitAmount != null || investmentProfitCny != null || totalProfitCny != null
      || knownNativeProfitAmount != null || knownInvestmentProfitCny != null || knownTotalProfitCny != null;
    return {
      currency,
      nativeProfitAmount,
      investmentProfitCny: complete ? investmentProfitCny : null,
      fxEffectCny: complete ? fxEffectCny : null,
      totalProfitCny: complete ? totalProfitCny : null,
      knownNativeProfitAmount,
      knownInvestmentProfitCny,
      knownFxEffectCny,
      knownTotalProfitCny,
      gapReasons: mergeProfitAttributionGapReasons(values.map((value) => value.gapReasons)),
      knownEpisodeCount: bucketKnown,
      totalEpisodeCount: bucketTotal,
      status: complete ? "complete" as const
        : partial ? "partial" as const
          : "unavailable" as const,
    };
  }));
  const allBucketsComplete = buckets.length > 0 && buckets.every((bucket) => bucket.status === "complete");
  const investmentProfitCny = allBucketsComplete
    ? buckets.reduce((sum, bucket) => sum + Number(bucket.investmentProfitCny), 0) : null;
  const fxEffectCny = allBucketsComplete
    ? buckets.reduce((sum, bucket) => sum + Number(bucket.fxEffectCny), 0) : null;
  const totalProfitCny = allBucketsComplete
    ? buckets.reduce((sum, bucket) => sum + Number(bucket.totalProfitCny), 0) : null;
  const sumKnownBuckets = (select: (bucket: NativeProfitAttributionBucket) => number | null | undefined) => {
    const known = buckets.flatMap((bucket) => {
      const value = select(bucket);
      return value != null && Number.isFinite(value) ? [Number(value)] : [];
    });
    return known.length ? known.reduce((sum, value) => sum + value, 0) : null;
  };
  const knownInvestmentProfitCny = sumKnownBuckets((bucket) => bucket.knownInvestmentProfitCny);
  const knownFxEffectCny = sumKnownBuckets((bucket) => bucket.knownFxEffectCny);
  const knownTotalProfitCny = sumKnownBuckets((bucket) => bucket.knownTotalProfitCny);
  const expected = options.expectedTotalProfitCny;
  const tolerance = expected == null ? 0
    : Math.max(.01, Math.abs(expected) * profitAttributionTolerance);
  const reconciles = expected == null || totalProfitCny != null && Math.abs(totalProfitCny - expected) <= tolerance;
  const reconciliationMismatch = expected != null && totalProfitCny != null
    && Math.abs(totalProfitCny - expected) > tolerance;
  const complete = totalEpisodeCount > 0 && knownEpisodeCount >= totalEpisodeCount
    && allBucketsComplete && investmentProfitCny != null && fxEffectCny != null
    && totalProfitCny != null && reconciles;
  const status = complete ? "complete" as const
    : buckets.some((bucket) => bucket.status !== "unavailable") ? "partial" as const
      : "unavailable" as const;
  return {
    buckets,
    investmentProfitCny: complete ? investmentProfitCny : null,
    fxEffectCny: complete ? fxEffectCny : null,
    totalProfitCny: complete ? totalProfitCny : null,
    knownInvestmentProfitCny,
    knownFxEffectCny,
    knownTotalProfitCny,
    gapReasons: mergeProfitAttributionGapReasons([
      ...inputs.map((input) => input.gapReasons),
      ...buckets.map((bucket) => bucket.gapReasons),
      reconciliationMismatch ? ["reconciliation-mismatch"] : [],
      !complete && knownEpisodeCount < totalEpisodeCount
        && !inputs.some((input) => input.gapReasons?.length) ? ["evidence-missing"] : [],
    ]),
    knownEpisodeCount,
    totalEpisodeCount,
    ...(notApplicableEpisodeCount > 0 ? { notApplicableEpisodeCount, notApplicableReasons } : {}),
    status,
  };
}

function unavailableNativeReturn(totalPositions = 0, currency: Currency | null = null): NativeReturnEvidence {
  return {
    currency,
    profitAmount: null,
    capitalBaseAmount: null,
    returnRate: null,
    measuredPositions: 0,
    totalPositions,
    status: "unavailable",
  };
}

function nativeReturnForPositionPoint(
  point: PositionPerformancePoint,
  currency: Currency,
  nativeFlows: NativeFlowBreakdown,
  weight: number,
): NativeReturnEvidence {
  const periodProfit = point.periodProfit;
  const currencies = new Set(nativeFlows.buckets.map((bucket) => bucket.currency));
  const nativeCapitalBase = periodProfit == null || point.marketValue == null
    ? null
    : point.marketValue + point.reducedAmount + point.dividendIncome - periodProfit;
  const complete = nativeFlows.status === "complete"
    && [...currencies].every((value) => value === currency)
    && periodProfit != null
    && point.periodReturnRate != null
    && nativeCapitalBase != null
    && Number.isFinite(nativeCapitalBase)
    && nativeCapitalBase > 0;
  if (!complete || periodProfit == null) return unavailableNativeReturn(1, currencies.size <= 1 ? currency : null);
  return {
    currency,
    profitAmount: periodProfit * weight,
    capitalBaseAmount: nativeCapitalBase * weight,
    returnRate: point.periodReturnRate,
    measuredPositions: 1,
    totalPositions: 1,
    status: "complete",
  };
}

function mergeNativeReturnEvidence(
  values: readonly NativeReturnEvidence[],
  mode: "parallel" | "period" = "parallel",
): NativeReturnEvidence {
  if (!values.length) return unavailableNativeReturn();
  const totalPositions = mode === "parallel"
    ? values.reduce((sum, value) => sum + value.totalPositions, 0)
    : Math.max(0, ...values.map((value) => value.totalPositions));
  const measuredPositions = mode === "parallel"
    ? values.reduce((sum, value) => sum + value.measuredPositions, 0)
    : Math.min(...values.map((value) => value.measuredPositions));
  const currencies = new Set(values.map((value) => value.currency).filter((currency): currency is Currency => Boolean(currency)));
  const complete = values.every((value) => value.status === "complete"
    && value.currency != null
    && value.returnRate != null
    && value.profitAmount != null
    && value.capitalBaseAmount != null)
    && currencies.size === 1
    && measuredPositions >= totalPositions
    && totalPositions > 0;
  const currency = currencies.size === 1 ? [...currencies][0] : null;
  if (!complete) return {
    ...unavailableNativeReturn(totalPositions, currency),
    measuredPositions: Math.min(measuredPositions, totalPositions),
  };
  const profitAmount = values.reduce((sum, value) => sum + Number(value.profitAmount), 0);
  const capitalBaseAmount = mode === "parallel"
    ? values.reduce((sum, value) => sum + Number(value.capitalBaseAmount), 0)
    : values[0].capitalBaseAmount;
  const returnRate = mode === "parallel"
    ? Number(capitalBaseAmount) > 0 ? profitAmount / Number(capitalBaseAmount) * 100 : null
    : (values.reduce((factor, value) => factor * (1 + Number(value.returnRate) / 100), 1) - 1) * 100;
  return {
    currency,
    profitAmount,
    capitalBaseAmount,
    returnRate,
    measuredPositions,
    totalPositions,
    status: returnRate == null || !Number.isFinite(returnRate) ? "unavailable" : "complete",
  };
}

function committedNativeCapitalBase(points: NormalizedPositionPoint[]) {
  const ordered = points.slice().sort((left, right) => left.date.localeCompare(right.date));
  const firstMeasuredIndex = ordered.findIndex((point) => (point.nativeReturn.capitalBaseAmount ?? 0) > 0);
  const added = (point: NormalizedPositionPoint) => point.nativeFlows.buckets
    .filter((bucket) => bucket.currency === point.nativeReturn.currency)
    .reduce((sum, bucket) => sum + bucket.addedAmount, 0);
  if (firstMeasuredIndex < 0) return ordered.reduce((sum, point) => sum + added(point), 0);
  const additionsThroughBase = ordered.slice(0, firstMeasuredIndex + 1).reduce((sum, point) => sum + added(point), 0);
  const laterAdditions = ordered.slice(firstMeasuredIndex + 1).reduce((sum, point) => sum + added(point), 0);
  return Math.max(Number(ordered[firstMeasuredIndex].nativeReturn.capitalBaseAmount), additionsThroughBase) + laterAdditions;
}

function nativeRangeReturn(
  positions: readonly ImportedPosition[],
  pointsByPosition: Map<string, NormalizedPositionPoint[]>,
): NativeReturnEvidence {
  positions = positions.filter((position) => !isTigerCashPosition(position));
  if (!positions.length) return unavailableNativeReturn();
  const currencies = new Set(positions.map((position) => position.currency));
  const currency = currencies.size === 1 ? [...currencies][0] : null;
  let profitAmount = 0;
  let capitalBaseAmount = 0;
  let measuredPositions = 0;
  for (const position of positions) {
    const rows = pointsByPosition.get(position.id) ?? [];
    const returnRows = rows.filter((point) => point.valuationEvidence);
    if (!returnRows.length || returnRows.some((point) => point.nativeReturn.status !== "complete")) continue;
    const positionCapitalBase = committedNativeCapitalBase(rows);
    if (!(positionCapitalBase > 0)) continue;
    measuredPositions += 1;
    profitAmount += returnRows.reduce((sum, point) => sum + Number(point.nativeReturn.profitAmount), 0);
    capitalBaseAmount += positionCapitalBase;
  }
  const complete = currency != null && measuredPositions === positions.length && capitalBaseAmount > 0;
  if (!complete) return {
    ...unavailableNativeReturn(positions.length, currency),
    measuredPositions,
  };
  return {
    currency,
    profitAmount,
    capitalBaseAmount,
    returnRate: profitAmount / capitalBaseAmount * 100,
    measuredPositions,
    totalPositions: positions.length,
    status: "complete",
  };
}

function profitAttributionForRange(
  positions: readonly ImportedPosition[],
  pointsByPosition: Map<string, NormalizedPositionPoint[]>,
) {
  const attributablePositions = positions.filter((position) => !isTigerCashPosition(position));
  const notApplicableEpisodeCount = positions.length - attributablePositions.length;
  const byPosition = attributablePositions.map((position) => {
    const rows = (pointsByPosition.get(position.id) ?? []).filter((point) => point.valuationEvidence);
    if (!rows.length) return unavailableProfitAttribution(1, position.currency);
    return mergeProfitAttributionBreakdowns(rows.map((point) => point.profitAttribution), {
      mode: "period",
      expectedTotalProfitCny: rows.reduce((sum, point) => sum + point.profitCny, 0),
      totalEpisodeCount: 1,
    });
  });
  const expectedTotalProfitCny = attributablePositions.reduce((sum, position) =>
    sum + (pointsByPosition.get(position.id) ?? [])
      .filter((point) => point.valuationEvidence)
      .reduce((positionSum, point) => positionSum + point.profitCny, 0), 0);
  const result = mergeProfitAttributionBreakdowns(byPosition, {
    mode: "parallel",
    expectedTotalProfitCny,
    totalEpisodeCount: attributablePositions.length,
  });
  return notApplicableEpisodeCount > 0 ? {
    ...result,
    notApplicableEpisodeCount,
    notApplicableReasons: ["non-yield-cash" as const],
  } : result;
}

export const portfolioAnalyticsRangeOptions = [
  { key: "1", months: 1 },
  { key: "3", months: 3 },
  { key: "6", months: 6 },
  { key: "ytd", months: null },
  { key: "12", months: 12 },
  { key: "24", months: 24 },
  { key: "36", months: 36 },
  { key: "60", months: 60 },
  { key: "120", months: 120 },
  { key: "all", months: 0 },
] as const satisfies ReadonlyArray<{ key: PortfolioAnalyticsRangeKey; months: number | null }>;

const palette = ["#2f6cf4", "#0e9f93", "#d28b16", "#cf536e", "#725bdc", "#3484a8", "#53795f", "#a65b39"];

export const UNGROUPED_ANALYTICS_FILTER = "__ungrouped__";
export const UNKNOWN_ANALYTICS_FILTER = "__unknown__";

function historicalGroupIdentity(groupId: string | null, groupName: string | null) {
  return groupId ?? (groupName ? `legacy:${groupName}` : UNGROUPED_ANALYTICS_FILTER);
}

export function filterPortfolioAnalyticsPositions(
  positions: ImportedPosition[],
  filters?: PortfolioAnalyticsFilters,
  assetExposures: AssetExposure[] = [],
) {
  if (!filters || !Object.values(filters).some(Boolean)) return positions;
  const exposuresByPosition = new Map<string, AssetExposure[]>();
  for (const exposure of assetExposures) {
    exposuresByPosition.set(exposure.positionId, [
      ...(exposuresByPosition.get(exposure.positionId) ?? []),
      exposure,
    ]);
  }
  const positionIds = filters.positionIds?.length
    ? new Set(filters.positionIds)
    : filters.positionWeightWindows
      ? new Set(Object.keys(filters.positionWeightWindows))
      : null;
  return positions.filter((position) => {
    const groupId = position.groupId ?? UNGROUPED_ANALYTICS_FILTER;
    const assetType = position.underlyingType ?? UNKNOWN_ANALYTICS_FILTER;
    const platformId = position.platformId ?? position.source;
    const through = position.latestNavDate || position.dailyReturnDate || position.lastTradeDate || position.firstTradeDate || "9999-12-31";
    const resolvedUnderlying = filters.underlying
      ? resolvePositionUnderlyingExposures(position, exposuresByPosition.get(position.id) ?? [], through)
      : null;
    const underlyingMatches = !filters.underlying || resolvedUnderlying?.items.some((item) =>
      filters.underlying === item.canonicalKey
      || filters.underlying === item.displayName
      || underlyingExposureKey(filters.underlying) === item.canonicalKey);
    return (!positionIds || positionIds.has(position.id))
      && (!filters.groupId || filters.groupId === groupId)
      && (!filters.assetType || filters.assetType === assetType)
      && (!filters.currency || filters.currency === position.currency)
      && (!filters.platformId || filters.platformId === platformId)
      && underlyingMatches
      && (!filters.scope || filters.scope === "all"
        || (filters.scope === "closed" ? position.closed : !position.closed));
  });
}

function stableColor(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  return palette[hash % palette.length];
}

function startOfWeek(dateText: string) {
  const date = new Date(`${dateText}T00:00:00Z`);
  const weekday = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() - weekday + 1);
  return date.toISOString().slice(0, 10);
}

function periodKey(date: string, period: PeriodName) {
  if (period === "daily") return date;
  if (period === "weekly") return startOfWeek(date);
  if (period === "monthly") return date.slice(0, 7);
  if (period === "quarterly") return `${date.slice(0, 4)}-Q${Math.floor((Number(date.slice(5, 7)) - 1) / 3) + 1}`;
  return date.slice(0, 4);
}

function rangeStarts(through: string) {
  const month = Number(through.slice(5, 7));
  return {
    day: through,
    week: startOfWeek(through),
    month: through.slice(0, 7),
    quarter: `${through.slice(0, 4)}-${String(Math.floor((month - 1) / 3) * 3 + 1).padStart(2, "0")}`,
    year: through.slice(0, 4),
  };
}

export function portfolioRangeKey(months: number): PortfolioAnalyticsRangeKey {
  return months <= 0 ? "all" : String(months) as PortfolioAnalyticsRangeKey;
}

export function portfolioRangeCutoff(through: string, months: number) {
  if (!through || months <= 0) return null;
  return shiftCalendarMonths(through, -months);
}

function analyticsPeriodStart(date: string) {
  if (/^\d{4}-Q[1-4]$/.test(date)) {
    const quarter = Number(date.slice(-1));
    return `${date.slice(0, 4)}-${String((quarter - 1) * 3 + 1).padStart(2, "0")}-01`;
  }
  if (/^\d{4}$/.test(date)) return `${date}-01-01`;
  if (/^\d{4}-\d{2}$/.test(date)) return `${date}-01`;
  return date.slice(0, 10);
}

function analyticsPeriodEnd(date: string, period: PeriodName) {
  const start = analyticsPeriodStart(date);
  if (period === "daily") return start;
  const end = new Date(`${start}T00:00:00Z`);
  if (period === "weekly") end.setUTCDate(end.getUTCDate() + 6);
  if (period === "monthly") end.setUTCMonth(end.getUTCMonth() + 1, 0);
  if (period === "quarterly") end.setUTCMonth(end.getUTCMonth() + 3, 0);
  if (period === "yearly") end.setUTCFullYear(end.getUTCFullYear() + 1, 0, 0);
  return end.toISOString().slice(0, 10);
}

export function filterPortfolioAnalyticsSeries(
  points: PortfolioAnalyticsPoint[],
  months: number,
  through: string | null,
  period: PeriodName = "daily",
) {
  const cutoff = through ? portfolioRangeCutoff(through, months) : null;
  let cumulativeProfitCny = 0;
  return points
    .filter((point) => !cutoff || analyticsPeriodEnd(point.date, period) >= cutoff)
    .map((point) => {
      cumulativeProfitCny += point.profitCny;
      return { ...point, cumulativeProfitCny };
    });
}

export function filterPortfolioAnalyticsSeriesByRange(
  points: PortfolioAnalyticsPoint[],
  value: TimeRangeValue,
  through: string | null,
  period: PeriodName = "daily",
) {
  const first = points[0] ? analyticsPeriodStart(points[0].date) : null;
  const range = resolveTimeRange(value, through, first);
  let cumulativeProfitCny = 0;
  return points
    .filter((point) => {
      const start = analyticsPeriodStart(point.date);
      const end = analyticsPeriodEnd(point.date, period);
      return (!range.from || end >= range.from) && (!range.to || start <= range.to);
    })
    .map((point) => {
      cumulativeProfitCny += point.profitCny;
      return { ...point, cumulativeProfitCny };
    });
}

export function summarizePortfolioAnalyticsSeries(points: PortfolioAnalyticsPoint[]): PortfolioAnalyticsRange {
  const valuationPoints = points.filter((point) => point.valuationEvidence !== false);
  const profitCny = valuationPoints.reduce((sum, point) => sum + point.profitCny, 0);
  const addedAmountCny = valuationPoints.reduce((sum, point) => sum + point.addedAmountCny, 0);
  const reducedAmountCny = valuationPoints.reduce((sum, point) => sum + point.reducedAmountCny, 0);
  const incomeCny = valuationPoints.reduce((sum, point) => sum + (point.incomeCny ?? 0), 0);
  const nativeFlows = mergeNativeFlowBreakdowns(points.map((point) => point.nativeFlows));
  const nativeReturn = mergeNativeReturnEvidence(
    valuationPoints.map((point) => point.nativeReturn ?? unavailableNativeReturn(point.measuredPositions)),
    "period",
  );
  const profitAttribution = mergeProfitAttributionBreakdowns(
    valuationPoints.map((point) => point.profitAttribution ?? unavailableProfitAttribution(point.measuredPositions)),
    {
      mode: "period",
      totalEpisodeCount: Math.max(0, ...valuationPoints.map((point) =>
        point.profitAttribution?.totalEpisodeCount ?? point.measuredPositions)),
    },
  );
  const capitalCandidates = valuationPoints.flatMap((point) => point.capitalBaseCny && point.capitalBaseCny > 0 ? [point.capitalBaseCny] : []);
  const capitalBaseCny = capitalCandidates.length ? Math.max(addedAmountCny, ...capitalCandidates) : addedAmountCny > 0 ? addedAmountCny : null;
  return {
    from: points[0] ? dateLabelBounds(points[0].date)?.start ?? null : null,
    to: points.at(-1) ? dateLabelBounds(points.at(-1)!.date)?.end ?? null : null,
    profitCny,
    addedAmountCny,
    reducedAmountCny,
    netFlowCny: addedAmountCny - reducedAmountCny,
    incomeCny,
    openingValueCny: null,
    closingValueCny: null,
    boundaryMeasuredPositions: 0,
    boundaryTotalPositions: 0,
    capitalBaseCny,
    returnRate: capitalBaseCny == null ? null : profitCny / capitalBaseCny * 100,
    measuredPositions: Math.max(0, ...valuationPoints.map((point) => point.measuredPositions)),
    totalPositions: Math.max(0, ...valuationPoints.map((point) => point.measuredPositions)),
    marketValueMeasuredPositions: Math.max(0, ...valuationPoints.map((point) => point.measuredPositions)),
    marketValueTotalPositions: Math.max(0, ...valuationPoints.map((point) => point.measuredPositions)),
    flowMeasuredPositions: Math.max(0, ...valuationPoints.map((point) => point.measuredPositions)),
    flowTotalPositions: Math.max(0, ...valuationPoints.map((point) => point.measuredPositions)),
    nativeFlows,
    nativeReturn,
    profitAttribution,
    dimensions: { asset: [], underlying: [], group: [], riskCurrency: [], platform: [] },
  };
}

export function portfolioAnnualPerformance(points: PortfolioAnalyticsPoint[], through: string | null) {
  const currentYear = through?.slice(0, 4) ?? null;
  const currentYearComplete = through?.slice(5, 10) === "12-31";
  const rows = points.map((point) => ({
    ...point,
    returnRate: point.returnRate ?? (point.nativeReturn?.status === "complete" ? point.nativeReturn.returnRate : null),
    returnCurrency: point.returnRate != null ? "CNY" as const
      : point.nativeReturn?.status === "complete" ? point.nativeReturn.currency : null,
    complete: point.date !== currentYear || currentYearComplete,
  }));
  const completed = rows.filter((point) => point.complete && point.returnRate != null);
  const positiveYears = completed.filter((point) => point.returnRate! > 0).length;
  return {
    rows,
    positiveYearRate: completed.length ? positiveYears / completed.length * 100 : null,
    averageReturnRate: completed.length ? completed.reduce((sum, point) => sum + point.returnRate!, 0) / completed.length : null,
    bestReturnRate: completed.length ? Math.max(...completed.map((point) => point.returnRate!)) : null,
    worstReturnRate: completed.length ? Math.min(...completed.map((point) => point.returnRate!)) : null,
  };
}

function dimensionIdentity(position: ImportedPosition, name: DimensionName) {
  if (name === "asset") return position.fundName || position.fundCode || "未命名资产";
  if (name === "underlying") return unpenetratedExposureLabel(position.trackedIndex || position.underlyingType);
  if (name === "group") return position.groupName || "未分组";
  if (name === "riskCurrency") return position.riskCurrency || position.currency || "未识别币种";
  return position.platformName || "未识别平台";
}

function committedCapitalBase(points: NormalizedPositionPoint[]) {
  const ordered = points.slice().sort((left, right) => left.date.localeCompare(right.date));
  const firstMeasuredIndex = ordered.findIndex((point) => point.capitalBaseCny > 0);
  if (firstMeasuredIndex < 0) return ordered.reduce((sum, point) => sum + point.addedAmountCny, 0);
  const additionsThroughBase = ordered
    .slice(0, firstMeasuredIndex + 1)
    .reduce((sum, point) => sum + point.addedAmountCny, 0);
  const laterAdditions = ordered
    .slice(firstMeasuredIndex + 1)
    .reduce((sum, point) => sum + point.addedAmountCny, 0);
  return Math.max(ordered[firstMeasuredIndex].capitalBaseCny, additionsThroughBase) + laterAdditions;
}

function groupYearlyAnalytics(
  positions: ImportedPosition[],
  groups: PositionGroup[],
  normalizedPointsByPosition: Map<string, NormalizedPositionPoint[]>,
): PortfolioAnalytics["groupYearly"] {
  type Accumulator = Omit<PortfolioAnalytics["groupYearly"][number], "capitalBaseCny" | "returnRate" | "netFlowCny" | "profitAttribution"> & {
    capitalBaseCny: number;
  };
  const groupById = new Map(groups.map((group) => [group.id, group]));
  const rows = new Map<string, Accumulator>();
  const positionsByRow = new Map<string, ImportedPosition[]>();
  const pointsByRow = new Map<string, Map<string, NormalizedPositionPoint[]>>();

  function ensureRow(groupId: string, year: number) {
    const definition = groupById.get(groupId);
    const targetReturnRate = definition?.annualTargets.find((target) => target.targetYear === year)?.targetReturnRate ?? definition?.annualizedTargetReturnRate ?? null;
    const key = `${groupId}:${year}`;
    const current = rows.get(key) ?? {
      year,
      groupId,
      label: definition?.name ?? (groupId === "ungrouped" ? "未分组" : "未知分组"),
      color: definition?.color ?? stableColor(`group:${groupId}`),
      positionCount: 0,
      measuredPositionCount: 0,
      profitCny: 0,
      addedAmountCny: 0,
      reducedAmountCny: 0,
      capitalBaseCny: 0,
      nativeFlows: emptyNativeFlowBreakdown(),
      targetReturnRate,
    };
    rows.set(key, current);
    return current;
  }

  for (const position of positions) {
    const points = normalizedPointsByPosition.get(position.id) ?? [];
    const effectiveGroupYears = new Map<string, {
      groupId: string;
      year: number;
      values: NormalizedPositionPoint[];
    }>();
    for (const point of points) {
      const year = Number(point.date.slice(0, 4));
      if (!Number.isInteger(year)) continue;
      const groupId = point.groupId ?? "ungrouped";
      const key = `${groupId}:${year}`;
      const segment = effectiveGroupYears.get(key) ?? { groupId, year, values: [] };
      segment.values.push(point);
      effectiveGroupYears.set(key, segment);
    }
    for (const [key, segment] of effectiveGroupYears) {
      const { groupId, year, values } = segment;
      const current = ensureRow(groupId, year);
      current.positionCount += 1;
      const positionMeasured = values.some((point) => point.measured);
      if (positionMeasured) {
        current.measuredPositionCount += 1;
        current.profitCny += values.reduce((sum, point) => sum + point.profitCny, 0);
        current.capitalBaseCny += committedCapitalBase(values);
      }
      current.addedAmountCny += values.reduce((sum, point) => sum + point.addedAmountCny, 0);
      current.reducedAmountCny += values.reduce((sum, point) => sum + point.reducedAmountCny, 0);
      current.nativeFlows = mergeNativeFlowBreakdowns([current.nativeFlows, ...values.map((point) => point.nativeFlows)], {
        knownEpisodeCount: current.positionCount,
        totalEpisodeCount: current.positionCount,
      });
      const rowPositions = positionsByRow.get(key) ?? [];
      if (!rowPositions.some((item) => item.id === position.id)) rowPositions.push(position);
      positionsByRow.set(key, rowPositions);
      const rowPoints = pointsByRow.get(key) ?? new Map<string, NormalizedPositionPoint[]>();
      rowPoints.set(position.id, values);
      pointsByRow.set(key, rowPoints);
    }
  }

  for (const group of groups) {
    for (const target of group.annualTargets) ensureRow(group.id, target.targetYear);
  }

  return [...rows.values()].map((row) => {
    const measurable = row.measuredPositionCount > 0 && row.capitalBaseCny > 0;
    const key = `${row.groupId}:${row.year}`;
    return {
      ...row,
      capitalBaseCny: row.capitalBaseCny > 0 ? row.capitalBaseCny : null,
      netFlowCny: row.addedAmountCny - row.reducedAmountCny,
      returnRate: measurable ? row.profitCny / row.capitalBaseCny * 100 : null,
      nativeReturn: nativeRangeReturn(positionsByRow.get(key) ?? [], pointsByRow.get(key) ?? new Map()),
      profitAttribution: profitAttributionForRange(
        positionsByRow.get(key) ?? [],
        pointsByRow.get(key) ?? new Map(),
      ),
    };
  }).sort((left, right) => left.year - right.year || left.label.localeCompare(right.label, "zh-CN"));
}

function aggregateSeries(
  dailyRows: Array<Omit<PortfolioAnalyticsPoint, "date" | "cumulativeProfitCny"> & { date: string }>,
  period: PeriodName,
) {
  const buckets = new Map<string, Omit<PortfolioAnalyticsPoint, "date" | "cumulativeProfitCny">>();
  for (const row of dailyRows) {
    const key = periodKey(row.date, period);
    const existing = buckets.get(key);
    const current = existing ?? {
      profitCny: 0,
      addedAmountCny: 0,
      reducedAmountCny: 0,
      netFlowCny: 0,
      incomeCny: 0,
      capitalBaseCny: 0,
      returnRate: null,
      measuredPositions: 0,
      nativeFlows: emptyNativeFlowBreakdown(),
      nativeReturn: unavailableNativeReturn(),
      profitAttribution: unavailableProfitAttribution(),
      valuationEvidence: false,
    };
    const hadValuationEvidence = current.valuationEvidence !== false;
    const rowHasValuationEvidence = row.valuationEvidence !== false;
    current.profitCny += row.profitCny;
    current.addedAmountCny += row.addedAmountCny;
    current.reducedAmountCny += row.reducedAmountCny;
    current.netFlowCny += row.netFlowCny;
    current.incomeCny += row.incomeCny ?? 0;
    if (!(current.capitalBaseCny && current.capitalBaseCny > 0)) {
      current.capitalBaseCny = Math.max(row.capitalBaseCny ?? 0, row.addedAmountCny);
    } else {
      current.capitalBaseCny += row.addedAmountCny;
    }
    current.measuredPositions = Math.max(current.measuredPositions, row.measuredPositions);
    current.nativeFlows = mergeNativeFlowBreakdowns([current.nativeFlows, row.nativeFlows]);
    if (rowHasValuationEvidence) current.profitAttribution = hadValuationEvidence
      ? mergeProfitAttributionBreakdowns([
        current.profitAttribution ?? unavailableProfitAttribution(current.measuredPositions),
        row.profitAttribution ?? unavailableProfitAttribution(row.measuredPositions),
      ], { mode: "period" })
      : row.profitAttribution ?? unavailableProfitAttribution(row.measuredPositions);
    if (rowHasValuationEvidence) current.nativeReturn = hadValuationEvidence
      ? mergeNativeReturnEvidence([
        current.nativeReturn ?? unavailableNativeReturn(current.measuredPositions),
        row.nativeReturn ?? unavailableNativeReturn(row.measuredPositions),
      ], "period")
      : row.nativeReturn ?? unavailableNativeReturn(row.measuredPositions);
    current.valuationEvidence = hadValuationEvidence || rowHasValuationEvidence;
    buckets.set(key, current);
  }
  let cumulativeProfitCny = 0;
  return [...buckets.entries()].sort(([left], [right]) => left.localeCompare(right)).map(([date, row]) => {
    cumulativeProfitCny += row.profitCny;
    const capitalBaseCny = row.capitalBaseCny && row.capitalBaseCny > 0 ? row.capitalBaseCny : null;
    return {
      ...row,
      date,
      cumulativeProfitCny,
      capitalBaseCny,
      returnRate: capitalBaseCny == null ? null : row.profitCny / capitalBaseCny * 100,
    } satisfies PortfolioAnalyticsPoint;
  });
}

export function aggregatePortfolioAnalyticsSeries(
  dailyPoints: PortfolioAnalyticsPoint[],
  period: PeriodName,
) {
  if (period === "daily") {
    let cumulativeProfitCny = 0;
    return dailyPoints.map((point) => {
      cumulativeProfitCny += point.profitCny;
      return { ...point, cumulativeProfitCny };
    });
  }
  return aggregateSeries(dailyPoints.map((point) => ({
    date: point.date,
    profitCny: point.profitCny,
    addedAmountCny: point.addedAmountCny,
    reducedAmountCny: point.reducedAmountCny,
    netFlowCny: point.netFlowCny,
    incomeCny: point.incomeCny ?? 0,
    capitalBaseCny: point.capitalBaseCny,
    returnRate: point.returnRate,
    measuredPositions: point.measuredPositions,
    nativeFlows: point.nativeFlows,
    nativeReturn: point.nativeReturn,
    profitAttribution: point.profitAttribution,
    valuationEvidence: point.valuationEvidence,
  })), period);
}

export function emptyPortfolioAnalytics(): PortfolioAnalytics {
  const emptyRange = (): PortfolioAnalyticsRange => ({
    from: null,
    to: null,
    profitCny: 0,
    addedAmountCny: 0,
    reducedAmountCny: 0,
    netFlowCny: 0,
    incomeCny: 0,
    openingValueCny: null,
    closingValueCny: null,
    boundaryMeasuredPositions: 0,
    boundaryTotalPositions: 0,
    capitalBaseCny: null,
    returnRate: null,
    measuredPositions: 0,
    totalPositions: 0,
    marketValueMeasuredPositions: 0,
    marketValueTotalPositions: 0,
    flowMeasuredPositions: 0,
    flowTotalPositions: 0,
    nativeFlows: emptyNativeFlowBreakdown(),
    nativeReturn: unavailableNativeReturn(),
    profitAttribution: unavailableProfitAttribution(),
    dimensions: { asset: [], underlying: [], group: [], riskCurrency: [], platform: [] },
  });
  return {
    through: null,
    series: { daily: [], weekly: [], monthly: [], quarterly: [], yearly: [] },
    dimensions: { asset: [], underlying: [], group: [], riskCurrency: [], platform: [] },
    groupYearly: [],
    ranges: Object.fromEntries(
      portfolioAnalyticsRangeOptions.map((option) => [option.key, emptyRange()]),
    ) as Record<PortfolioAnalyticsRangeKey, PortfolioAnalyticsRange>,
  };
}

export function compactPortfolioAnalyticsForOverview(
  analytics: PortfolioAnalytics,
  range?: { from: string; to: string },
): PortfolioAnalytics {
  const daily = analytics.series.daily
    .filter((point) => (!range?.from || point.date >= range.from) && (!range?.to || point.date <= range.to))
    .map(({ nativeReturn, profitAttribution, ...point }) => {
      void nativeReturn;
      void profitAttribution;
      return point;
    });
  return {
    through: analytics.through,
    series: {
      // The overview chart consumes the exact daily profit/capital/flow facts.
      // Per-day evidence remains available from the on-demand analytics route;
      // the selected range keeps its complete aggregate evidence below.
      daily,
      weekly: [],
      monthly: [],
      quarterly: [],
      yearly: [],
    },
    dimensions: { asset: [], underlying: [], group: [], riskCurrency: [], platform: [] },
    ranges: ({ ytd: analytics.ranges.ytd } as unknown) as PortfolioAnalytics["ranges"],
    groupYearly: [],
  };
}

function portfolioAnalyticsWeightForDate(
  positionId: string,
  date: string,
  positionWeights?: Record<string, number>,
  positionWeightWindows?: PortfolioAnalyticsFilters["positionWeightWindows"],
) {
  const windows = positionWeightWindows?.[positionId];
  if (windows) {
    const active = windows.filter((window) => window.from <= date && (!window.to || window.to > date));
    return Math.max(0, Math.min(100, active.reduce((sum, window) => sum + Number(window.weight), 0))) / 100;
  }
  return Math.max(0, Math.min(100, positionWeights?.[positionId] ?? 100)) / 100;
}

export function portfolioAnalyticsThrough(
  positions: ImportedPosition[],
  performanceByPosition: Map<string, PositionPerformancePoint[]>,
  positionWeights?: Record<string, number>,
  positionWeightWindows?: PortfolioAnalyticsFilters["positionWeightWindows"],
) {
  let through: string | null = null;
  for (const [positionId, points] of performanceByPosition) {
    for (const point of points) {
      if (portfolioAnalyticsWeightForDate(positionId, point.date, positionWeights, positionWeightWindows) > 0
        && (!through || point.date > through)) through = point.date;
      for (const flow of point.nativeFlowDates ?? []) {
        if (portfolioAnalyticsWeightForDate(positionId, flow.date, positionWeights, positionWeightWindows) > 0
          && (!through || flow.date > through)) through = flow.date;
      }
    }
  }
  if (through) return through;
  for (const position of positions) {
    const date = position.latestNavDate || position.dailyReturnDate || null;
    if (date && (!through || date > through)) through = date;
  }
  return through;
}

export function buildPortfolioAnalytics(
  positions: ImportedPosition[],
  performanceByPosition: Map<string, PositionPerformancePoint[]>,
  groups: PositionGroup[] = [],
  customRange?: { from: string; to: string },
  positionWeights?: Record<string, number>,
  positionWeightWindows?: PortfolioAnalyticsFilters["positionWeightWindows"],
  assetExposures: AssetExposure[] = [],
): PortfolioAnalytics {
  const exposuresByPosition = new Map<string, AssetExposure[]>();
  for (const exposure of assetExposures) {
    exposuresByPosition.set(exposure.positionId, [
      ...(exposuresByPosition.get(exposure.positionId) ?? []),
      exposure,
    ]);
  }
  const underlyingFor = (position: ImportedPosition, date: string) =>
    resolvePositionUnderlyingExposures(position, exposuresByPosition.get(position.id) ?? [], date);
  const weightForDate = (positionId: string, date: string) =>
    portfolioAnalyticsWeightForDate(positionId, date, positionWeights, positionWeightWindows);
  const groupForDate = (position: ImportedPosition, date: string) => {
    const windows = positionWeightWindows?.[position.id]?.filter((window) =>
      window.from <= date && (!window.to || window.to > date) && window.groupName);
    const identities = new Map(windows?.map((window) => [
      historicalGroupIdentity(window.groupId ?? null, window.groupName ?? null),
      window,
    ]) ?? []);
    if (identities.size === 1) {
      const window = [...identities.values()][0];
      return { groupId: window.groupId ?? null, groupName: window.groupName ?? null, groupColor: window.groupColor ?? null };
    }
    if (identities.size > 1) return { groupId: null, groupName: "待分组", groupColor: "#8491A5" };
    return { groupId: position.groupId, groupName: position.groupName, groupColor: position.groupColor };
  };
  const through = portfolioAnalyticsThrough(
    positions,
    performanceByPosition,
    positionWeights,
    positionWeightWindows,
  );
  if (!through) {
    return {
      ...emptyPortfolioAnalytics(),
      groupYearly: groupYearlyAnalytics(positions, groups, new Map()),
    };
  }
  const analyticsThrough = through;
  const starts = rangeStarts(through);
  const dailyBuckets = new Map<string, Omit<PortfolioAnalyticsPoint, "date" | "cumulativeProfitCny">>();
  const dailyAttributableProfitCny = new Map<string, number>();
  const positionPeriodValues = new Map<string, PortfolioAnalyticsDimension["profitCny"]>();
  const normalizedPointsByPosition = new Map<string, NormalizedPositionPoint[]>();
  const emptyDailyBucket = (): Omit<PortfolioAnalyticsPoint, "date" | "cumulativeProfitCny"> => ({
    profitCny: 0,
    addedAmountCny: 0,
    reducedAmountCny: 0,
    netFlowCny: 0,
    incomeCny: 0,
    capitalBaseCny: 0,
    returnRate: null,
    measuredPositions: 0,
    nativeFlows: emptyNativeFlowBreakdown(),
    nativeReturn: unavailableNativeReturn(),
    profitAttribution: unavailableProfitAttribution(),
    valuationEvidence: false,
  });

  for (const position of positions) {
    const yieldAttributionApplicable = !isTigerCashPosition(position);
    const currentWeight = weightForDate(position.id, analyticsThrough);
    const rate = position.currency === "CNY" ? 1 : position.valuationRateCny;
    const periodValues = { day: 0, week: 0, month: 0, quarter: 0, year: 0 };
    const points = performanceByPosition.get(position.id) ?? [];
    const normalizedPoints: NormalizedPositionPoint[] = [];
    for (const point of points) {
      const sourceNativeFlows = point.nativeFlows ?? nativeFlowBreakdownFromAmounts({
        currency: position.currency,
        addedAmount: point.addedAmount,
        reducedAmount: point.reducedAmount,
        incomeAmount: point.dividendIncome,
        movementCount: [point.addedAmount, point.reducedAmount, point.dividendIncome]
          .filter((value) => Math.abs(value) > .000001).length,
      });
      for (const datedFlow of point.nativeFlowDates ?? []) {
        const flowWeight = weightForDate(position.id, datedFlow.date);
        if (flowWeight <= 0) continue;
        const nativeFlows = yieldAttributionApplicable
          ? scaleNativeFlowBreakdown(datedFlow.nativeFlows, flowWeight)
          : emptyNativeFlowBreakdown();
        const flowHasActivity = nativeFlows.buckets.some((bucket) => Math.abs(bucket.addedAmount) > .000001
          || Math.abs(bucket.reducedAmount) > .000001 || Math.abs(bucket.incomeAmount) > .000001);
        normalizedPoints.push({
          date: datedFlow.date,
          profitCny: 0,
          addedAmountCny: 0,
          reducedAmountCny: 0,
          incomeCny: 0,
          capitalBaseCny: 0,
          capitalBaseMeasured: false,
          marketValue: null,
          marketValueCny: null,
          valuationRateCny: null,
          valuationRateAt: null,
          valuationRateSource: null,
          valuationRateMethod: null,
          quantity: null,
          measured: false,
          flowMeasured: true,
          flowHasActivity,
          historicalEvidence: true,
          nativeFlows,
          nativeReturn: unavailableNativeReturn(),
          profitAttribution: unavailableProfitAttribution(),
          valuationEvidence: false,
          ...groupForDate(position, datedFlow.date),
        });
        const dailyFlow = dailyBuckets.get(datedFlow.date) ?? emptyDailyBucket();
        dailyFlow.nativeFlows = mergeNativeFlowBreakdowns([dailyFlow.nativeFlows, nativeFlows], {
          knownEpisodeCount: dailyFlow.nativeFlows.knownEpisodeCount + nativeFlows.knownEpisodeCount,
          totalEpisodeCount: dailyFlow.nativeFlows.totalEpisodeCount + nativeFlows.totalEpisodeCount,
        });
        dailyBuckets.set(datedFlow.date, dailyFlow);
      }
      const weight = weightForDate(position.id, point.date);
      if (weight <= 0) continue;
      const measuredProfitCny = point.periodProfitCny
        ?? (position.currency === "CNY" ? point.periodProfit : null);
      const profitCny = (measuredProfitCny ?? 0) * weight;
      const addedAmountCnyValue = point.addedAmountCny
        ?? (position.currency === "CNY" ? point.addedAmount : null);
      const reducedAmountCnyValue = point.reducedAmountCny
        ?? (position.currency === "CNY" ? point.reducedAmount : null);
      const incomeCnyValue = point.dividendIncomeCny
        ?? (position.currency === "CNY" ? point.dividendIncome : null);
      const flowHasActivity = Math.abs(point.addedAmount) > .000001
        || Math.abs(point.reducedAmount) > .000001
        || Math.abs(point.dividendIncome) > .000001
        || Math.abs(point.addedAmountCny ?? 0) > .000001
        || Math.abs(point.reducedAmountCny ?? 0) > .000001
        || Math.abs(point.dividendIncomeCny ?? 0) > .000001;
      const flowMeasured = (addedAmountCnyValue != null && reducedAmountCnyValue != null && incomeCnyValue != null)
        || (!flowHasActivity && point.addedAmount === 0 && point.reducedAmount === 0 && point.dividendIncome === 0);
      const addedAmountCny = (addedAmountCnyValue ?? 0) * weight;
      const reducedAmountCny = (reducedAmountCnyValue ?? 0) * weight;
      const incomeCny = (incomeCnyValue ?? 0) * weight;
      const pointNativeFlows = yieldAttributionApplicable
        ? scaleNativeFlowBreakdown(sourceNativeFlows, weight)
        : emptyNativeFlowBreakdown();
      const pointGroup = groupForDate(position, point.date);
      const returnAttributionMismatch = (point.nativeFlowDates ?? []).some((datedFlow) => {
        const flowGroup = groupForDate(position, datedFlow.date);
        return Math.abs(weightForDate(position.id, datedFlow.date) - weight) > .000000001
          || historicalGroupIdentity(flowGroup.groupId, flowGroup.groupName)
            !== historicalGroupIdentity(pointGroup.groupId, pointGroup.groupName);
      });
      const pointNativeReturn = !yieldAttributionApplicable
        ? unavailableNativeReturn()
        : returnAttributionMismatch
          ? unavailableNativeReturn(1, position.currency)
          : nativeReturnForPositionPoint(point, position.currency, pointNativeFlows, weight);
      const pointProfitAttribution = yieldAttributionApplicable
        ? profitAttributionForPositionPoint({
          currency: position.currency,
          nativeProfitAmount: point.periodProfit
            ?? (position.currency === "CNY" ? point.periodProfitCny : null),
          totalProfitCny: measuredProfitCny,
          valuationRateCny: point.valuationRateCny ?? (position.currency === "CNY" ? 1 : null),
          weight,
          attributable: !returnAttributionMismatch,
        })
        : nonYieldCashProfitAttribution();
      const hasDatedFlowOnValuationDate = point.nativeFlowDates?.some((flow) => flow.date === point.date) ?? false;
      const displayedNativeFlows = !yieldAttributionApplicable
        ? emptyNativeFlowBreakdown()
        : point.nativeFlowDates
        ? emptyNativeFlowBreakdown({
          knownEpisodeCount: hasDatedFlowOnValuationDate ? 0 : 1,
          totalEpisodeCount: hasDatedFlowOnValuationDate ? 0 : 1,
        })
        : pointNativeFlows;
      const inferredCapitalBase = position.currency === "CNY"
        && point.periodReturnRate != null && Math.abs(point.periodReturnRate) > 0.0000001;
      const inferredBase = point.capitalBaseCny != null
        ? point.capitalBaseCny * weight
        : (inferredCapitalBase
          ? Math.abs(profitCny / (Number(point.periodReturnRate) / 100))
          : 0);
      normalizedPoints.push({
        date: point.date,
        profitCny,
        addedAmountCny,
        reducedAmountCny,
        incomeCny,
        capitalBaseCny: inferredBase,
        capitalBaseMeasured: point.capitalBaseCny != null || inferredCapitalBase,
        marketValue: (point.marketValue ?? (position.currency === "CNY" ? point.marketValueCny : null)) == null
          ? null
          : Number(point.marketValue ?? point.marketValueCny) * weight,
        marketValueCny: (point.marketValueCny
          ?? (position.currency === "CNY" ? point.marketValue : null)) == null
          ? null
          : Number(point.marketValueCny ?? point.marketValue) * weight,
        valuationRateCny: point.valuationRateCny ?? (position.currency === "CNY" ? 1 : null),
        valuationRateAt: point.valuationRateAt ?? point.date,
        valuationRateSource: point.valuationRateSource ?? null,
        valuationRateMethod: point.valuationRateMethod ?? (position.currency === "CNY" ? "parity" : null),
        quantity: point.quantity == null ? null : point.quantity * weight,
        measured: measuredProfitCny != null,
        flowMeasured,
        flowHasActivity,
        historicalEvidence: true,
        nativeFlows: displayedNativeFlows,
        nativeReturn: pointNativeReturn,
        profitAttribution: pointProfitAttribution,
        valuationEvidence: true,
        ...pointGroup,
      });
      const existing = dailyBuckets.get(point.date);
      const current = existing ?? emptyDailyBucket();
      const hadValuationEvidence = current.valuationEvidence !== false;
      current.profitCny += profitCny;
      current.addedAmountCny += addedAmountCny;
      current.reducedAmountCny += reducedAmountCny;
      current.netFlowCny += addedAmountCny - reducedAmountCny;
      current.incomeCny += incomeCny;
      current.capitalBaseCny = (current.capitalBaseCny ?? 0) + inferredBase;
      current.measuredPositions += measuredProfitCny == null ? 0 : 1;
      current.nativeFlows = mergeNativeFlowBreakdowns([current.nativeFlows, displayedNativeFlows], {
        knownEpisodeCount: current.nativeFlows.knownEpisodeCount + displayedNativeFlows.knownEpisodeCount,
        totalEpisodeCount: current.nativeFlows.totalEpisodeCount + displayedNativeFlows.totalEpisodeCount,
      });
      if (yieldAttributionApplicable) current.nativeReturn = hadValuationEvidence
        ? mergeNativeReturnEvidence([current.nativeReturn ?? unavailableNativeReturn(), pointNativeReturn])
        : pointNativeReturn;
      const attributableProfitCny = (dailyAttributableProfitCny.get(point.date) ?? 0)
        + (yieldAttributionApplicable ? profitCny : 0);
      current.profitAttribution = hadValuationEvidence
        ? mergeProfitAttributionBreakdowns([
          current.profitAttribution ?? unavailableProfitAttribution(),
          pointProfitAttribution,
        ], { mode: "parallel", expectedTotalProfitCny: attributableProfitCny })
        : pointProfitAttribution;
      dailyAttributableProfitCny.set(point.date, attributableProfitCny);
      current.valuationEvidence = true;
      dailyBuckets.set(point.date, current);
      if (point.date === starts.day) periodValues.day += profitCny;
      if (point.date >= starts.week) periodValues.week += profitCny;
      if (point.date.slice(0, 7) === starts.month) periodValues.month += profitCny;
      if (point.date >= starts.quarter) periodValues.quarter += profitCny;
      if (point.date.startsWith(starts.year)) periodValues.year += profitCny;
    }
    if (!normalizedPoints.length && currentWeight > 0 && rate != null && rate > 0 && position.dailyReturnDate === through && position.dailyProfit != null) {
      const profitCny = position.dailyProfit * rate * currentWeight;
      const inferredBase = position.dailyReturnRate != null && Math.abs(position.dailyReturnRate) > 0.0000001
        ? Math.abs(profitCny / (position.dailyReturnRate / 100))
        : 0;
      periodValues.day += profitCny;
      periodValues.week += profitCny;
      periodValues.month += profitCny;
      periodValues.quarter += profitCny;
      periodValues.year += profitCny;
      const fallbackNativeCapitalBase = position.dailyReturnRate != null
        ? Math.abs(position.dailyReturnRate) > .0000001 && position.dailyProfit != null
          ? Math.abs(position.dailyProfit / (position.dailyReturnRate / 100)) * currentWeight
          : position.dailyProfit === 0 && position.marketValue != null
            ? position.marketValue * currentWeight
            : null
        : null;
      const fallbackNativeReturn: NativeReturnEvidence = yieldAttributionApplicable
        && fallbackNativeCapitalBase != null && fallbackNativeCapitalBase > 0
        ? {
          currency: position.currency,
          profitAmount: Number(position.dailyProfit) * currentWeight,
          capitalBaseAmount: fallbackNativeCapitalBase,
          returnRate: position.dailyReturnRate,
          measuredPositions: 1,
          totalPositions: 1,
          status: "complete",
        }
        : unavailableNativeReturn(1, position.currency);
      const fallbackProfitAttribution = yieldAttributionApplicable
        ? profitAttributionForPositionPoint({
          currency: position.currency,
          nativeProfitAmount: position.dailyProfit,
          totalProfitCny: profitCny / currentWeight,
          valuationRateCny: rate,
          weight: currentWeight,
        })
        : nonYieldCashProfitAttribution();
      const existing = dailyBuckets.get(through);
      const current = existing ?? emptyDailyBucket();
      const hadValuationEvidence = current.valuationEvidence !== false;
      current.profitCny += profitCny;
      current.capitalBaseCny = (current.capitalBaseCny ?? 0) + inferredBase;
      current.measuredPositions += 1;
      const fallbackNativeFlows = yieldAttributionApplicable
        ? emptyNativeFlowBreakdown({ knownEpisodeCount: 1, totalEpisodeCount: 1 })
        : emptyNativeFlowBreakdown();
      current.nativeFlows = mergeNativeFlowBreakdowns([current.nativeFlows, fallbackNativeFlows]);
      if (yieldAttributionApplicable) current.nativeReturn = hadValuationEvidence
        ? mergeNativeReturnEvidence([current.nativeReturn ?? unavailableNativeReturn(), fallbackNativeReturn])
        : fallbackNativeReturn;
      const attributableProfitCny = (dailyAttributableProfitCny.get(through) ?? 0)
        + (yieldAttributionApplicable ? profitCny : 0);
      current.profitAttribution = hadValuationEvidence
        ? mergeProfitAttributionBreakdowns([
          current.profitAttribution ?? unavailableProfitAttribution(),
          fallbackProfitAttribution,
        ], { mode: "parallel", expectedTotalProfitCny: attributableProfitCny })
        : fallbackProfitAttribution;
      dailyAttributableProfitCny.set(through, attributableProfitCny);
      current.valuationEvidence = true;
      dailyBuckets.set(through, current);
      normalizedPoints.push({
        date: through,
        profitCny,
        addedAmountCny: 0,
        reducedAmountCny: 0,
        incomeCny: 0,
        capitalBaseCny: inferredBase,
        capitalBaseMeasured: inferredBase > 0,
        marketValue: (position.marketValue ?? (position.currency === "CNY" ? position.marketValueCny : null)) == null
          ? null
          : Number(position.marketValue ?? position.marketValueCny) * currentWeight,
        marketValueCny: (position.marketValueCny
          ?? (position.currency === "CNY" ? position.marketValue : null)) == null
          ? null
          : Number(position.marketValueCny ?? position.marketValue) * currentWeight,
        valuationRateCny: position.valuationRateCny ?? (position.currency === "CNY" ? 1 : null),
        valuationRateAt: position.valuationRateAt ?? through,
        valuationRateSource: position.valuationRateSource,
        valuationRateMethod: position.currency === "CNY" ? "parity" : "observed",
        quantity: position.quantity == null ? null : position.quantity * currentWeight,
        measured: true,
        flowMeasured: true,
        flowHasActivity: false,
        historicalEvidence: false,
        nativeFlows: fallbackNativeFlows,
        nativeReturn: fallbackNativeReturn,
        profitAttribution: fallbackProfitAttribution,
        valuationEvidence: true,
        ...groupForDate(position, through),
      });
    }
    normalizedPointsByPosition.set(position.id, normalizedPoints);
    positionPeriodValues.set(position.id, periodValues);
  }

  const dailyRows = [...dailyBuckets.entries()].sort(([left], [right]) => left.localeCompare(right)).map(([date, row]) => ({ ...row, date }));
  const daily = aggregateSeries(dailyRows, "daily");
  const weightFor = (positionId: string) => weightForDate(positionId, analyticsThrough);
  const totalMarketValue = positions.filter((position) => !position.closed).reduce((sum, position) => sum + Math.max(0, position.marketValueCny ?? 0) * weightFor(position.id), 0);
  const totalProfit = positions.reduce((sum, position) => sum + (position.totalProfitCny ?? 0) * weightFor(position.id), 0);

  function dimensions(name: DimensionName) {
    const groups = new Map<string, PortfolioAnalyticsDimension>();
    for (const position of positions) {
      const weight = weightFor(position.id);
      const exposureSet = name === "underlying" ? underlyingFor(position, analyticsThrough) : null;
      const segments = exposureSet
        ? exposureSet.items.map((exposure) => ({
          identity: exposure.canonicalKey,
          label: exposure.displayName,
          factor: exposure.weight / 100,
          exposureStatus: exposureSet.status,
          exposureKnownWeight: exposureSet.knownWeight,
        }))
        : [{
          identity: dimensionIdentity(position, name),
          label: dimensionIdentity(position, name),
          factor: 1,
          exposureStatus: undefined,
          exposureKnownWeight: undefined,
        }];
      for (const segment of segments) {
        const key = `${name}:${segment.identity}`;
        const current = groups.get(key) ?? {
          key,
          label: segment.label,
          color: position.groupColor && name === "group" ? position.groupColor : stableColor(key),
          assetCount: 0,
          marketValueCny: 0,
          remainingCostCny: 0,
          totalProfitCny: 0,
          allocationPercent: 0,
          contributionPercent: null,
          returnRate: null,
          profitCny: { day: 0, week: 0, month: 0, quarter: 0, year: 0 },
          addedAmountCny: 0,
          reducedAmountCny: 0,
          nativeFlows: emptyNativeFlowBreakdown(),
          ...(segment.exposureStatus ? {
            exposureStatus: segment.exposureStatus,
            exposureKnownWeight: segment.exposureKnownWeight,
          } : {}),
        };
        current.assetCount += 1;
        if (segment.exposureStatus) {
          current.exposureStatus = current.exposureStatus
            ? lessCertainExposureStatus(current.exposureStatus, segment.exposureStatus)
            : segment.exposureStatus;
          current.exposureKnownWeight = Math.min(
            current.exposureKnownWeight ?? 100,
            segment.exposureKnownWeight ?? 0,
          );
        }
        if (!position.closed) {
          current.marketValueCny += Math.max(0, position.marketValueCny ?? 0) * weight * segment.factor;
          current.remainingCostCny += Math.max(0, position.remainingCostCny ?? 0) * weight * segment.factor;
        }
        current.totalProfitCny += (position.totalProfitCny ?? 0) * weight * segment.factor;
        current.addedAmountCny += (position.addedAmountCny
          ?? (position.currency === "CNY" ? position.addedAmount : 0)) * weight * segment.factor;
        current.reducedAmountCny += (position.reducedAmountCny
          ?? (position.currency === "CNY" ? position.reducedAmount : 0)) * weight * segment.factor;
        current.nativeFlows = mergeNativeFlowBreakdowns([
          current.nativeFlows,
          scaleNativeFlowBreakdown(nativeFlowBreakdownFromAmounts({
            currency: position.currency,
            addedAmount: position.addedAmount,
            reducedAmount: position.reducedAmount,
            movementCount: position.orderCount,
          }), weight * segment.factor),
        ], { knownEpisodeCount: current.assetCount, totalEpisodeCount: current.assetCount });
        const period = positionPeriodValues.get(position.id);
        if (period) for (const periodName of Object.keys(period) as Array<keyof typeof period>) {
          current.profitCny[periodName] += period[periodName] * segment.factor;
        }
        groups.set(key, current);
      }
    }
    return [...groups.values()].map((item) => ({
      ...item,
      allocationPercent: totalMarketValue > 0 ? item.marketValueCny / totalMarketValue * 100 : 0,
      contributionPercent: Math.abs(totalProfit) > 0.0000001 ? item.totalProfitCny / Math.abs(totalProfit) * 100 : null,
      returnRate: item.remainingCostCny > 0 ? item.totalProfitCny / item.remainingCostCny * 100 : null,
    })).sort((left, right) => right.marketValueCny - left.marketValueCny);
  }

  function rangeSnapshot(months: number | null, bounds?: { from: string; to: string }): PortfolioAnalyticsRange {
    const cutoff = bounds?.from
      ?? (months == null ? `${analyticsThrough.slice(0, 4)}-01-01` : portfolioRangeCutoff(analyticsThrough, months));
    const end = bounds?.to ?? null;
    const snapshotThrough = end ?? analyticsThrough;
    const rangeRowsByPosition = new Map([...normalizedPointsByPosition].map(([positionId, points]) => [
      positionId,
      points.filter((point) => (!cutoff || point.date >= cutoff) && (!end || point.date <= end)),
    ]));
    const rows = [...rangeRowsByPosition.values()].flat();
    const measuredPositionIds = new Set<string>();
    const marketValueMeasuredPositionIds = new Set<string>();
    const flowMeasuredPositionIds = new Set<string>();
    for (const [positionId] of normalizedPointsByPosition) {
      const rangeRows = rangeRowsByPosition.get(positionId) ?? [];
      if (rangeRows.some((point) => point.measured)) measuredPositionIds.add(positionId);
      if (rangeRows.some((point) => point.marketValueCny != null)) marketValueMeasuredPositionIds.add(positionId);
      if (rangeRows.length && rangeRows.every((point) => point.flowMeasured)) flowMeasuredPositionIds.add(positionId);
    }
    const profitCny = rows.reduce((sum, point) => sum + point.profitCny, 0);
    const addedAmountCny = rows.reduce((sum, point) => sum + point.addedAmountCny, 0);
    const reducedAmountCny = rows.reduce((sum, point) => sum + point.reducedAmountCny, 0);
    const incomeCny = rows.reduce((sum, point) => sum + point.incomeCny, 0);
    const rawCapitalBaseCny = [...normalizedPointsByPosition.values()]
      .reduce((sum, points) => sum + committedCapitalBase(
        points.filter((point) => (!cutoff || point.date >= cutoff) && (!end || point.date <= end)),
      ), 0);
    const capitalBaseCny = rawCapitalBaseCny > 0 ? rawCapitalBaseCny : null;
    const relevantPositions = positions.filter((position) => {
      const windows = positionWeightWindows?.[position.id];
      if (windows) {
        return windows.some((window) => window.from <= snapshotThrough && (!window.to || !cutoff || window.to > cutoff));
      }
      return Math.max(0, Math.min(100, positionWeights?.[position.id] ?? 100)) > 0;
    });
    const relevantPositionIds = new Set(relevantPositions.map((position) => position.id));
    const nativeReturn = nativeRangeReturn(relevantPositions, rangeRowsByPosition);
    const profitAttribution = profitAttributionForRange(relevantPositions, rangeRowsByPosition);
    const nativeFlowPositions = relevantPositions.filter((position) => !isTigerCashPosition(position));
    const nativeFlowKnownPositions = nativeFlowPositions.filter((position) => {
      const positionRows = (normalizedPointsByPosition.get(position.id) ?? [])
        .filter((point) => (!cutoff || point.date >= cutoff) && (!end || point.date <= end));
      return positionRows.filter((point) => point.nativeFlows.totalEpisodeCount > 0)
        .every((point) => point.nativeFlows.status !== "unavailable");
    }).length;
    const nativeFlowRows = nativeFlowPositions.flatMap((position) => rangeRowsByPosition.get(position.id) ?? []);
    const nativeFlows = mergeNativeFlowBreakdowns(nativeFlowRows.map((point) => point.nativeFlows), {
      knownEpisodeCount: nativeFlowKnownPositions,
      totalEpisodeCount: nativeFlowPositions.length,
    });
    const boundaryRows = relevantPositions.map((position) => {
      const positionRows = (rangeRowsByPosition.get(position.id) ?? [])
        .filter((point) => point.valuationEvidence)
        .sort((left, right) => left.date.localeCompare(right.date));
      const first = positionRows[0] ?? null;
      const last = positionRows.at(-1) ?? null;
      const openingValueCny = first?.capitalBaseMeasured && first.flowMeasured
        ? Math.max(0, first.capitalBaseCny - first.addedAmountCny)
        : null;
      const closingValueCny = last?.marketValueCny ?? null;
      const complete = positionRows.length > 0
        && positionRows.every((point) => point.historicalEvidence && point.measured && point.flowMeasured)
        && openingValueCny != null
        && closingValueCny != null;
      return { openingValueCny, closingValueCny, complete };
    });
    const boundaryMeasuredPositions = boundaryRows.filter((row) => row.complete).length;
    const boundaryTotalPositions = boundaryRows.length;
    const openingValueCny = boundaryTotalPositions > 0
      && boundaryRows.every((row) => row.openingValueCny != null)
      ? boundaryRows.reduce((sum, row) => sum + Number(row.openingValueCny), 0)
      : null;
    const closingValueCny = boundaryTotalPositions > 0
      && boundaryRows.every((row) => row.closingValueCny != null)
      ? boundaryRows.reduce((sum, row) => sum + Number(row.closingValueCny), 0)
      : null;

    function rangeDimensions(name: DimensionName) {
      const groups = new Map<string, PortfolioAnalyticsRangeDimension>();
      const positionsByDimension = new Map<string, ImportedPosition[]>();
      const rowsByDimension = new Map<string, Map<string, NormalizedPositionPoint[]>>();
      const valuationsByDimension = new Map<string, NativeValuationInput[]>();
      for (const position of positions) {
        const positionRows = rangeRowsByPosition.get(position.id) ?? [];
        if (!positionRows.some((point) => point.measured || point.flowHasActivity || point.nativeReturn.status === "complete")) continue;
        const historicalGroupRows = new Map<string, {
          groupId: string | null;
          label: string;
          color: string;
          rows: NormalizedPositionPoint[];
        }>();
        if (name === "group") for (const point of positionRows) {
          const label = point.groupName ?? "待分组";
          const identity = historicalGroupIdentity(point.groupId, point.groupName);
          const segment = historicalGroupRows.get(identity) ?? {
            groupId: point.groupId,
            label,
            color: point.groupColor ?? "#8491A5",
            rows: [],
          };
          segment.rows.push(point);
          historicalGroupRows.set(identity, segment);
        }
        const historicalUnderlyingRows = new Map<string, {
          label: string;
          rows: NormalizedPositionPoint[];
          exposureStatus: ExposureStatus;
          exposureKnownWeight: number;
        }>();
        if (name === "underlying") for (const point of positionRows) {
          const resolved = underlyingFor(position, point.date);
          for (const exposure of resolved.items) {
            const segment = historicalUnderlyingRows.get(exposure.canonicalKey) ?? {
              label: exposure.displayName,
              rows: [],
              exposureStatus: resolved.status,
              exposureKnownWeight: resolved.knownWeight,
            };
            segment.rows.push(scaleNormalizedPositionPoint(point, exposure.weight / 100));
            segment.exposureStatus = lessCertainExposureStatus(segment.exposureStatus, resolved.status);
            segment.exposureKnownWeight = Math.min(segment.exposureKnownWeight, resolved.knownWeight);
            historicalUnderlyingRows.set(exposure.canonicalKey, segment);
          }
        }
        const segments: Array<{
          identity: string;
          groupId: string | null;
          label: string;
          color: string | null;
          rows: NormalizedPositionPoint[];
          exposureStatus?: ExposureStatus;
          exposureKnownWeight?: number;
        }> = name === "group"
          ? [...historicalGroupRows].map(([identity, segment]) => ({ identity, ...segment }))
          : name === "underlying"
            ? [...historicalUnderlyingRows].map(([identity, segment]) => ({
              identity,
              groupId: null,
              color: null,
              ...segment,
            }))
            : [{
              identity: dimensionIdentity(position, name),
              groupId: null,
              label: dimensionIdentity(position, name),
              color: null,
              rows: positionRows,
            }];
        for (const segment of segments) {
          const orderedRows = segment.rows.slice().sort((left, right) => left.date.localeCompare(right.date));
          const latestValuedRow = orderedRows.findLast((point) => point.marketValueCny != null) ?? null;
          const latestNativeValuedRow = orderedRows.findLast((point) => point.marketValue != null) ?? null;
          const currentGroup = groupForDate(position, analyticsThrough);
          const snapshotExposure = name === "underlying"
            ? underlyingFor(position, snapshotThrough).items.find((item) => item.canonicalKey === segment.identity)
            : null;
          const snapshotExposureWeight = name === "underlying" ? (snapshotExposure?.weight ?? 0) / 100 : 1;
          const canUseCurrentSnapshot = snapshotThrough >= analyticsThrough
            && (name !== "group" || segment.identity === historicalGroupIdentity(currentGroup.groupId, currentGroup.groupName))
            && (name !== "underlying" || snapshotExposureWeight > 0);
          const snapshotWeight = weightForDate(position.id, snapshotThrough);
          const latestQuantityRow = orderedRows.findLast((point) => point.quantity != null) ?? null;
          const exposureEndedBeforeCurrentSnapshot = name === "underlying"
            && snapshotThrough >= analyticsThrough
            && snapshotExposureWeight <= 0;
          const endMarketValueCny = exposureEndedBeforeCurrentSnapshot
            ? 0
            : canUseCurrentSnapshot
            ? position.marketValueCny == null
              ? latestValuedRow?.marketValueCny ?? null
              : position.marketValueCny * snapshotWeight * snapshotExposureWeight
            : latestValuedRow?.marketValueCny ?? null;
          const endMarketValue = exposureEndedBeforeCurrentSnapshot
            ? 0
            : canUseCurrentSnapshot
              ? (position.marketValue ?? (position.currency === "CNY" ? position.marketValueCny : null)) == null
                ? latestNativeValuedRow?.marketValue ?? null
                : Number(position.marketValue ?? position.marketValueCny) * snapshotWeight * snapshotExposureWeight
              : latestNativeValuedRow?.marketValue ?? null;
          const endRateEvidence = canUseCurrentSnapshot ? {
            valuationRateCny: position.valuationRateCny ?? (position.currency === "CNY" ? 1 : null),
            valuationRateAt: position.valuationRateAt ?? snapshotThrough,
            valuationRateSource: position.valuationRateSource,
            valuationRateMethod: position.currency === "CNY" ? "parity" as const : "observed" as const,
          } : {
            valuationRateCny: latestNativeValuedRow?.valuationRateCny ?? null,
            valuationRateAt: latestNativeValuedRow?.valuationRateAt ?? latestNativeValuedRow?.date ?? null,
            valuationRateSource: latestNativeValuedRow?.valuationRateSource ?? null,
            valuationRateMethod: latestNativeValuedRow?.valuationRateMethod ?? null,
          };
          const endQuantity = exposureEndedBeforeCurrentSnapshot
            ? 0
            : canUseCurrentSnapshot
            ? position.quantity == null
              ? latestQuantityRow?.quantity ?? null
              : position.quantity * snapshotWeight * snapshotExposureWeight
            : latestQuantityRow?.quantity ?? null;
          const holdingAtEnd = endMarketValueCny != null
            ? endMarketValueCny > .000001
            : endQuantity != null
              ? endQuantity > .000001
              : canUseCurrentSnapshot && !position.closed;
          const key = `${name}:${segment.identity}`;
          valuationsByDimension.set(key, [...(valuationsByDimension.get(key) ?? []), {
            currency: position.currency,
            marketValue: endMarketValue,
            marketValueCny: endMarketValueCny,
            ...endRateEvidence,
          }]);
          const dimensionPositions = positionsByDimension.get(key) ?? [];
          if (!dimensionPositions.some((item) => item.id === position.id)) dimensionPositions.push(position);
          positionsByDimension.set(key, dimensionPositions);
          const dimensionRows = rowsByDimension.get(key) ?? new Map<string, NormalizedPositionPoint[]>();
          dimensionRows.set(position.id, [...(dimensionRows.get(position.id) ?? []), ...segment.rows]);
          rowsByDimension.set(key, dimensionRows);
          const current = groups.get(key) ?? {
            key,
            label: segment.label,
            color: segment.color ?? stableColor(key),
            ...(name === "group" ? { groupId: segment.groupId } : {}),
            assetCount: 0,
            holdingAssetCount: 0,
            closedAssetCount: 0,
            marketValueMeasuredAssetCount: 0,
            marketValueTotalAssetCount: 0,
            marketValueCny: 0,
            marketValueAsOf: null,
            nativeValuation: emptyNativeValuationBreakdown(),
            profitCny: 0,
            addedAmountCny: 0,
            reducedAmountCny: 0,
            capitalBaseCny: null,
            returnRate: null,
            contributionPercent: null,
            flowMeasuredAssetCount: 0,
            flowTotalAssetCount: 0,
            nativeFlows: emptyNativeFlowBreakdown(),
            profitAttribution: unavailableProfitAttribution(),
            ...(segment.exposureStatus ? {
              exposureStatus: segment.exposureStatus,
              exposureKnownWeight: segment.exposureKnownWeight,
            } : {}),
          };
          current.assetCount += 1;
          current.marketValueTotalAssetCount += 1;
          current.flowTotalAssetCount += 1;
          if (segment.exposureStatus) {
            current.exposureStatus = current.exposureStatus
              ? lessCertainExposureStatus(current.exposureStatus, segment.exposureStatus)
              : segment.exposureStatus;
            current.exposureKnownWeight = Math.min(
              current.exposureKnownWeight ?? 100,
              segment.exposureKnownWeight ?? 0,
            );
          }
          if (holdingAtEnd) {
            current.holdingAssetCount += 1;
          } else current.closedAssetCount += 1;
          if (endMarketValueCny != null) {
            current.marketValueMeasuredAssetCount += 1;
            current.marketValueCny += Math.max(0, endMarketValueCny);
            if (!current.marketValueAsOf || (latestValuedRow?.date ?? snapshotThrough) > current.marketValueAsOf) {
              current.marketValueAsOf = latestValuedRow?.date ?? snapshotThrough;
            }
          }
          if (segment.rows.every((point) => point.flowMeasured)) current.flowMeasuredAssetCount += 1;
          current.profitCny += segment.rows.reduce((sum, point) => sum + point.profitCny, 0);
          current.addedAmountCny += segment.rows.reduce((sum, point) => sum + point.addedAmountCny, 0);
          current.reducedAmountCny += segment.rows.reduce((sum, point) => sum + point.reducedAmountCny, 0);
          const nativeKnownAssetCount = current.nativeFlows.knownEpisodeCount
            + (segment.rows.filter((point) => point.nativeFlows.totalEpisodeCount > 0)
              .every((point) => point.nativeFlows.status !== "unavailable") ? 1 : 0);
          current.nativeFlows = mergeNativeFlowBreakdowns([
            current.nativeFlows,
            ...segment.rows.map((point) => point.nativeFlows),
          ], {
            knownEpisodeCount: nativeKnownAssetCount,
            totalEpisodeCount: current.flowTotalAssetCount,
          });
          const positionCapitalBase = committedCapitalBase(segment.rows);
          current.capitalBaseCny = (current.capitalBaseCny ?? 0) + positionCapitalBase;
          groups.set(key, current);
        }
      }
      const values = [...groups.values()];
      const grossProfit = values.reduce((sum, item) => sum + Math.abs(item.profitCny), 0);
      return values.map((item) => {
        const itemCapitalBase = item.capitalBaseCny && item.capitalBaseCny > 0 ? item.capitalBaseCny : null;
        return {
          ...item,
          nativeValuation: buildNativeValuationBreakdown(valuationsByDimension.get(item.key) ?? []),
          capitalBaseCny: itemCapitalBase,
          returnRate: itemCapitalBase == null ? null : item.profitCny / itemCapitalBase * 100,
          nativeReturn: nativeRangeReturn(positionsByDimension.get(item.key) ?? [], rowsByDimension.get(item.key) ?? new Map()),
          profitAttribution: profitAttributionForRange(
            positionsByDimension.get(item.key) ?? [],
            rowsByDimension.get(item.key) ?? new Map(),
          ),
          contributionPercent: grossProfit > 0.0000001 ? item.profitCny / grossProfit * 100 : null,
        };
      }).sort((left, right) =>
        Math.abs(right.profitCny) - Math.abs(left.profitCny)
        || right.marketValueCny - left.marketValueCny
      );
    }

    return {
      from: bounds?.from ?? rows.map((point) => point.date).sort().at(0) ?? null,
      to: bounds?.to ?? rows.map((point) => point.date).sort().at(-1) ?? analyticsThrough,
      profitCny,
      addedAmountCny,
      reducedAmountCny,
      netFlowCny: addedAmountCny - reducedAmountCny,
      incomeCny,
      openingValueCny,
      closingValueCny,
      boundaryMeasuredPositions,
      boundaryTotalPositions,
      capitalBaseCny,
      returnRate: capitalBaseCny == null ? null : profitCny / capitalBaseCny * 100,
      measuredPositions: [...measuredPositionIds].filter((positionId) => relevantPositionIds.has(positionId)).length,
      totalPositions: relevantPositions.length,
      marketValueMeasuredPositions: [...marketValueMeasuredPositionIds]
        .filter((positionId) => relevantPositionIds.has(positionId)).length,
      marketValueTotalPositions: relevantPositions.length,
      flowMeasuredPositions: [...flowMeasuredPositionIds].filter((positionId) => relevantPositionIds.has(positionId)).length,
      flowTotalPositions: relevantPositions.length,
      nativeFlows,
      nativeReturn,
      profitAttribution,
      dimensions: {
        asset: rangeDimensions("asset"),
        underlying: rangeDimensions("underlying"),
        group: rangeDimensions("group"),
        riskCurrency: rangeDimensions("riskCurrency"),
        platform: rangeDimensions("platform"),
      },
    };
  }

  return {
    through,
    series: {
      daily,
      weekly: aggregateSeries(dailyRows, "weekly"),
      monthly: aggregateSeries(dailyRows, "monthly"),
      quarterly: aggregateSeries(dailyRows, "quarterly"),
      yearly: aggregateSeries(dailyRows, "yearly"),
    },
    dimensions: {
      asset: dimensions("asset"),
      underlying: dimensions("underlying"),
      group: dimensions("group"),
      riskCurrency: dimensions("riskCurrency"),
      platform: dimensions("platform"),
    },
    groupYearly: groupYearlyAnalytics(positions, groups, normalizedPointsByPosition),
    ranges: Object.fromEntries(
      portfolioAnalyticsRangeOptions.map((option) => [option.key, rangeSnapshot(option.months)]),
    ) as Record<PortfolioAnalyticsRangeKey, PortfolioAnalyticsRange>,
    ...(customRange ? { customRange: rangeSnapshot(0, customRange) } : {}),
  };
}
